import React, { useContext, useEffect, useReducer, useRef, useCallback } from 'react';
import {
	GameCategoriesContext,
	gameCategoriesInitialState,
	IFilterValues,
	TGameCategoriesCountOfItems,
	TGameCategoriesSortBy,
	TTypeOfModalWindow,
} from './gameCategoriesContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { gameCategoriesReducer } from './gameCategoriesReducer';
import { GameCategoriesActionTypes } from './gameCategoriesActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedGameCategories, deleteGameCategory } from 'backend/games';
import { AxiosResponse } from 'axios';
import { IGameCategories, IGetPaginatedGameCategories } from 'types';

const GameCategoriesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(gameCategoriesReducer, gameCategoriesInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IGameCategories>, IGetPaginatedGameCategories>(
		isMounted.current ? getPaginatedGameCategories : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: GameCategoriesActionTypes.SET_GAME_CATEGORIES,
				payload: {
					gameCategories: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteGameCategory(state.IDForDeletion);
			const filteredGameCategories = state.gameCategories.gameCategories.filter(
				(el) => el.id !== state.IDForDeletion,
			);
			dispatch({
				type: GameCategoriesActionTypes.SET_GAME_CATEGORIES,
				payload: {
					gameCategories: {
						gameCategoriesCount: state.gameCategories.gameCategoriesCount - 1,
						gameCategories: filteredGameCategories,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: GameCategoriesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.gameCategories]);

	const changeSearchString = (value: string) => {
		dispatch({
			type: GameCategoriesActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: GameCategoriesActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TGameCategoriesSortBy) => {
		dispatch({
			type: GameCategoriesActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TGameCategoriesCountOfItems) => {
		dispatch({
			type: GameCategoriesActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: GameCategoriesActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: GameCategoriesActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: GameCategoriesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: GameCategoriesActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
	};

	useEffect(() => {
		isMounted.current = true;
		refetch();
	}, [state.currentPage, state.countOfItems, state.searchString, state.sortBy]);

	return (
		<GameCategoriesContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				gameCategories: state.gameCategories,
				searchString: state.searchString,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</GameCategoriesContext.Provider>
	);
};

export default GameCategoriesState;
